<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllDpoz"></b-form-checkbox>
        <span @click="checkedAllDpoz = !checkedAllDpoz">Dojazdy pożarowe</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;"
            :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
:global(.dpoz-pointer-cursor),
:global(.dpoz-pointer-cursor *) {
  cursor: pointer !important;
}
</style>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";
import L from 'leaflet';
import { DataFilterExtension } from '@deck.gl/extensions';
import { DeckOverlay } from '@deck.gl-community/leaflet';
import { MapLasPathLayer } from '@/ts/MapLasPathLayer';

interface DpozPathBinaryData {
  length: number;
  startIndices: Uint32Array;
  attributes: {
    getPath: { value: Float32Array; size: 2 };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface DpozCardState {
  result: any[] | null;
  dpozResults: any[];
  dpozLayer: MapLasPathLayer | null;
  dpozOverlay: DeckOverlay | null;
  dpozHoverPolyline: L.Polyline;
  mapMouseMoveHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapMouseOutHandler: (() => void) | null;
  mapClickHandler: ((event: L.LeafletMouseEvent) => void) | null;
  hoveredDpozIndex: number | null;
}

declare module 'vue' {
  interface ComponentCustomProperties extends DpozCardState { }
}

export default defineComponent({
  extends: BaseComponent,
  data() {

    return {
      checkedAllDpoz: false,
      loading: false,
      expanded: false,
      active: true,
      inactive: false
    };
  },
  created() {
    this.result = null;
    this.dpozResults = [];
    this.dpozLayer = null;
    this.dpozOverlay = null;
    this.dpozHoverPolyline = L.polyline([], {
      color: 'yellow',
      weight: 4,
      opacity: 1,
      interactive: false,
    });
    this.mapMouseMoveHandler = null;
    this.mapMouseOutHandler = null;
    this.mapClickHandler = null;
    this.hoveredDpozIndex = null;
    EventBus.$on('downloadExternalData', () => this.downloadDpoz());
    EventBus.$on('toggleDpoz', () => {
      if (this.result) {
        this.checkedAllDpoz = !this.checkedAllDpoz;
      }
    });
    EventBus.$on('selectedAreaChanged', (newArea: string, oldArea: string) => this.onSelectedAreaChanged(newArea, oldArea));
  },
  beforeUnmount() {
    if (window.mainmap && this.mapMouseMoveHandler) window.mainmap.off('mousemove', this.mapMouseMoveHandler);
    if (window.mainmap && this.mapMouseOutHandler) window.mainmap.off('mouseout', this.mapMouseOutHandler);
    if (window.mainmap && this.mapClickHandler) window.mainmap.off('click', this.mapClickHandler);
    if (window.mainmap) {
      window.mainmap.getContainer().classList.remove('dpoz-pointer-cursor');
      window.mainmap.closePopup();
      this.hideDpozHoverPolyline();
      if (this.dpozOverlay) window.mainmap.removeLayer(this.dpozOverlay);
    }
  },
  methods: {

    isPointInSelectedArea(lat: number | string, lon: number | string) {
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      const plat = Number(lat);
      const plon = Number(lon);
      if (Number.isNaN(plat) || Number.isNaN(plon)) return false;
      return (plat >= minLat && plat <= maxLat && plon >= minLon && plon <= maxLon);
    },

    isPathInSelectedArea(path: any[]) {
      if (!path || !path.length) return false;
      const walk = (p: any): boolean => {
        if (Array.isArray(p)) {
          if (p.length >= 2 && (typeof p[0] === 'number' || typeof p[0] === 'string') && (typeof p[1] === 'number' || typeof p[1] === 'string')) {
            return this.isPointInSelectedArea(p[0], p[1]);
          } else {
            for (const sub of p) {
              if (walk(sub)) return true;
            }
          }
        } else if (p && typeof p === 'object') {
          const lat = p.lat ?? p.latitude ?? p[0];
          const lon = p.lon ?? p.lng ?? p.longitude ?? p[1];
          if (lat !== undefined && lon !== undefined && this.isPointInSelectedArea(lat, lon)) return true;
        }
        return false;
      }
      return walk(path);
    },

    normalizePath(path: any[]) {
      const points: any[] = [];
      if (!path) return points;
      const walk = (p: any) => {
        if (Array.isArray(p)) {
          if (p.length >= 2 && (typeof p[0] === 'number' || typeof p[0] === 'string') && (typeof p[1] === 'number' || typeof p[1] === 'string')) {
            points.push([Number(p[0]), Number(p[1])]);
          } else {
            for (const sub of p) walk(sub);
          }
        } else if (p && typeof p === 'object') {
          const lat = p.lat ?? p.latitude ?? p[0];
          const lon = p.lon ?? p.lng ?? p.longitude ?? p[1];
          if (lat !== undefined && lon !== undefined) points.push([Number(lat), Number(lon)]);
        }
      };
      walk(path);
      return points;
    },

    onSelectedAreaChanged(newArea: string, oldArea: string) {
      if (this.result) {
        this.processDpoz();
      }
    },
    downloadDpoz() {
      this.loading = true;
      const endPoint = window.apiHost + 'api/stringfield/dpoz/';
      this.downloadUrlWithCache(endPoint, 'dpoz', 'dpoz.txt', null, true).then((response) => {
        this.result = response[1].results;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('toursLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('toursDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('toursLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch((error) => {
        this.createAlert(AlertStatus.danger, this.$t('toursError').toString(), 2000);
      }).finally(() => {
        this.loading = false;
      });
    },
    processDpoz() {
      if (!this.result) return;
      this.hideDpozHoverPolyline();
      const tours: any[] = [];
      for (const responseItem of this.result) {
        const pathPoints = this.normalizePath(responseItem['paths']);
        if (!pathPoints || !pathPoints.length) continue;
        // only include segments that have at least one point inside selected area
        if (!this.isPathInSelectedArea(responseItem['paths'])) {
          continue;
        }
        tours.push({ ...responseItem, normalizedPath: pathPoints });
      }
      const positions: number[] = [];
      const startIndices: number[] = [];
      const filterValues: number[] = [];
      for (const item of tours) {
        startIndices.push(positions.length / 2);
        for (const [lat, lon] of item.normalizedPath) {
          positions.push(lon, lat);
          filterValues.push(item.is_active ? 1 : 0);
        }
      }
      startIndices.push(positions.length / 2);
      this.dpozResults = tours;
      this.hoveredDpozIndex = null;
      const pathIndices = Uint32Array.from(startIndices);
      const data: DpozPathBinaryData = {
        length: tours.length,
        startIndices: pathIndices,
        attributes: {
          getPath: { value: Float32Array.from(positions), size: 2 },
          getFilterValue: { value: Float32Array.from(filterValues), size: 1 },
        },
      };
      this.dpozLayer = new MapLasPathLayer({
        id: 'dpoz-paths',
        data,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: this.getDpozFilterRange(),
        positionFormat: 'XY',
        getColor: [255, 0, 0, 255],
        getWidth: 3,
        widthUnits: 'pixels',
        visible: this.checkedAllDpoz,
        pickable: false,
      });
      this.ensureDpozOverlay();
      this.onCheckedAllDpozChanged();
    },
    onCheckedAllDpozChanged() {
      this.hideDpozHoverPolyline();
      if (!this.dpozLayer) {
        if (this.result) this.processDpoz();
        return;
      }
      this.dpozLayer.setVisible(this.checkedAllDpoz);
      this.dpozLayer.setFilterRange(this.getDpozFilterRange());
      this.updateDpozOverlay();
    },
    getDpozFilterRange(): readonly [number, number] {
      if (this.active && this.inactive) return [0, 1];
      if (this.active) return [1, 1];
      if (this.inactive) return [0, 0];
      return [2, 2];
    },
    ensureDpozOverlay() {
      if (!window.mainmap) return;
      if (!this.dpozOverlay) {
        this.dpozOverlay = new DeckOverlay({ layers: [] });
        window.mainmap.addLayer(this.dpozOverlay);
      }
      if (!this.mapMouseMoveHandler) {
        this.mapMouseMoveHandler = (event: L.LeafletMouseEvent) => this.onDpozMapMouseMove(event);
        window.mainmap.on('mousemove', this.mapMouseMoveHandler);
      }
      if (!this.mapMouseOutHandler) {
        this.mapMouseOutHandler = () => this.onDpozMapMouseOut();
        window.mainmap.on('mouseout', this.mapMouseOutHandler);
      }
      if (!this.mapClickHandler) {
        this.mapClickHandler = (event: L.LeafletMouseEvent) => this.onDpozMapClick(event);
        window.mainmap.on('click', this.mapClickHandler);
      }
    },
    updateDpozOverlay() {
      this.ensureDpozOverlay();
      if (!this.dpozOverlay) return;
      this.dpozOverlay.setProps({ layers: this.dpozLayer ? [this.dpozLayer.getLayer()] : [] });
    },
    onDpozMapMouseMove(event: L.LeafletMouseEvent) {
      let index = this.checkedAllDpoz && this.dpozLayer
        ? this.dpozLayer.pick(event.latlng.lat, event.latlng.lng)
        : null;
      if (index !== null && !this.isDpozResultVisible(this.dpozResults[index])) index = null;
      if (index === null) {
        this.hideDpozHoverPolyline();
        return;
      }
      window.mainmap.getContainer().classList.add('dpoz-pointer-cursor');
      if (index === this.hoveredDpozIndex) return;
      const item = this.dpozResults[index];
      if (!item) return;
      this.dpozHoverPolyline.setLatLngs(item.normalizedPath);
      this.hoveredDpozIndex = index;
      if (!window.mainmap.hasLayer(this.dpozHoverPolyline)) this.dpozHoverPolyline.addTo(window.mainmap);
      this.dpozHoverPolyline.bringToFront();
    },
    onDpozMapMouseOut() {
      this.hideDpozHoverPolyline();
    },
    hideDpozHoverPolyline() {
      if (window.mainmap) {
        window.mainmap.getContainer().classList.remove('dpoz-pointer-cursor');
        if (window.mainmap.hasLayer(this.dpozHoverPolyline)) this.dpozHoverPolyline.removeFrom(window.mainmap);
      }
      this.hoveredDpozIndex = null;
    },
    onDpozMapClick(event: L.LeafletMouseEvent) {
      if (!this.checkedAllDpoz || !this.dpozLayer) return;
      const index = this.dpozLayer.pick(event.latlng.lat, event.latlng.lng);
      const item = index === null ? null : this.dpozResults[index];
      if (!item || !this.isDpozResultVisible(item)) return;
      const fieldsOrder = ['objectid', 'kod_ob', 'szer', 'nr_inw', 'nr_ppoz', 'a_year', 'lenght', 'is_active'];
      const content = document.createElement('div');
      for (const field of fieldsOrder) {
        if (!(field in item)) continue;
        const row = document.createElement('div');
        const label = document.createElement('strong');
        label.textContent = `${field}: `;
        row.append(label, document.createTextNode(String(item[field] ?? '')));
        content.append(row);
      }
      L.popup().setLatLng(event.latlng).setContent(content).openOn(window.mainmap);
    },
    isDpozResultVisible(item: any): boolean {
      return item?.is_active ? this.active : this.inactive;
    }
  },
  watch: {
    "checkedAllDpoz": [{
      handler: "onCheckedAllDpozChanged"
    }],
    "active": [{
      handler: "onCheckedAllDpozChanged"
    }],
    "inactive": [{
      handler: "onCheckedAllDpozChanged"
    }]
  }
})


</script>
